import type { CardDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-descriptor.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { EntityCardHistoryQueryInterface } from '@zaoblako/shared/common/dtos/history/entity-card-history-query.dto';
import type { BankDetailsUpdateInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank.details.update.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ContragentCollapseFormInterface } from '@zaoblako/shared/modules/client/dtos/contragent/collapse/contragent-collapse-form.dto';
import type { ContragentDuplicatesPageInterface } from '@zaoblako/shared/modules/client/dtos/contragent/collapse/contragent-duplicates-page.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { ContragentPageQueryInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-page-query.dto';
import type { ContragentPageInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-page.dto';
import type { ContragentRelatedObjectsStatInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-related-objects-stat.dto';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { ContragentBalanceSheetDefaultFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/balance-sheet-default/contragent-balance-sheet-default-find.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';

import { Injectable } from '@angular/core';
import { map, type Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ContragentService extends BaseService {
  protected baseUrl = '/basis/client/contragent';

  public get dropdownDescriptorApi(): string {
    return `${this.baseUrl}/dropdown-descriptor`;
  }

  public findBalanceSheetDefault(): Observable<ContragentFindInterface> {
    return this.http
      .get<ContragentBalanceSheetDefaultFindInterface>(`${this.baseUrl}/balance-sheet-default`)
      .pipe(map((response) => response?.contragent));
  }

  public setBalanceSheetDefault(contragent?: ContragentFindInterface): Observable<ContragentFindInterface> {
    return this.http
      .post<ContragentBalanceSheetDefaultFindInterface>(`${this.baseUrl}/balance-sheet-default`, { contragent })
      .pipe(map((response) => response?.contragent));
  }

  public getReportDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(`${this.baseUrl}/report-descriptor`);
  }

  public getCardDescriptor(): Observable<CardDescriptorInterface> {
    return this.http.get<CardDescriptorInterface>(`${this.baseUrl}/card-descriptor`);
  }

  public restoreOne(id: number): Observable<ContragentFindInterface> {
    return this.http.post<ContragentFindInterface>(`${this.baseUrl}/${id}/restore`, null);
  }

  public getAll(queryParams: ContragentPageQueryInterface): Observable<ContragentPageInterface> {
    return this.http.get<ContragentPageInterface>(this.baseUrl, { params: this.objectToHttpParams(queryParams) });
  }

  public bankDetailsFrom1C(contragentId: number): Observable<BankDetailsUpdateInterface[]> {
    return this.http.get<BankDetailsUpdateInterface[]>(`${this.baseUrl}/${contragentId}/bank-details-from-1c`);
  }

  public getPossibleDuplicates(queryParams: PaginationQueryInterface): Observable<ContragentDuplicatesPageInterface> {
    return this.http.get<ContragentDuplicatesPageInterface>(`${this.baseUrl}/possible-duplicates`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public collapse(id: number, body: ContragentCollapseFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/collapse`, body);
  }

  public getHistoryById(contragentId: number, params: EntityCardHistoryQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${contragentId}/history`, {
      params: this.objectToHttpParams(params)
    });
  }

  public getRelatedObjectsStat(id: number): Observable<ContragentRelatedObjectsStatInterface> {
    return this.http.get<ContragentRelatedObjectsStatInterface>(`${this.baseUrl}/${id}/related-objects-stat`);
  }

  public detectClientForOne(contragentId: number): Observable<ClientFindInterface[]> {
    return this.http.get<ClientFindInterface[]>(`${this.baseUrl}/${contragentId}/detect-client`);
  }

  public assignClientForOne(contragentId: number, clientId: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${contragentId}/assign-client/${clientId}`, {});
  }

  public assignExternalUserForOne(contragentId: number, externalUserId: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${contragentId}/assign-external-user/${externalUserId}`, {});
  }
}
